import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouteReuseStrategy } from '@angular/router';
import { LOCALSTORAGEKEYS } from '../../../../_constants/localStorageKeys';
import { roles } from '../../../../_constants/roles';
import { AppSettingService } from '../../../../_services/_appSetting/app-setting.service';
import { CommonService } from '../../../../_services/_common/common.service';
import { CustomRouteReuseStrategy } from '../../../../_services/route-reuse.strategy';
import { LocalService } from '../../../../_services/_storage/local.service';
import { SnackbarService } from '../../../../_services/snackbar/snackbar.service';

@Component({
    selector: 'lokos-financial-home',
    templateUrl: './financial-home.component.html',
    styleUrl: './financial-home.component.scss',
    standalone: false,
})
export class FinancialHomeComponent implements OnInit {
    collapse = false;
    isLoading = false;
    nationalPermissions: any = [];
    statePermissions: any = [];
    districtPermissions: any = [];
    blockPermissions: any = [];
    applicationPermissions: any = [];
    GEO_LOCATION: any;
    userProfile: any;

    constructor(
        private activateRoute: ActivatedRoute,
        private appSettingService: AppSettingService,
        private localService: LocalService,
        private router: Router,
        private snackbarService: SnackbarService,
        private commonService: CommonService,
        private routeReuseStrategy: RouteReuseStrategy
    ) {}

    ngOnInit(): void {
        this.router.events.subscribe(() => {
            if (
                (
                    this.routeReuseStrategy as CustomRouteReuseStrategy
                ).isRestoringNavigation?.()
            ) {
                return;
            }
            const ele = document.getElementById('outlet');
            if (ele) ele.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        });

        this.activateRoute.data.subscribe(async (data: any) => {
            if (data.isRoleConfigured) {
                this.GEO_LOCATION = data.isRoleConfigured[2];
                this.appSettingService.setGeoLocationAndClientIds(
                    data.isRoleConfigured[2],
                    'geoLocation'
                );
                this.appSettingService.setGeoLocationAndClientIds(
                    data.isRoleConfigured[3],
                    'clientIds'
                );
                const subRoles = (await this.commonService.getSubPermissions(
                    data.isRoleConfigured[0].sub
                )) as string[];
                const filteredSubRoles = subRoles.filter(
                    (res: string) => ![roles.SHG_BK, roles.VO_BK].includes(res)
                );
                this.setMetaInforMation(
                    data.isRoleConfigured[0].resource_access,
                    filteredSubRoles
                );
                this.localService.set(
                    LOCALSTORAGEKEYS.SUBROLES,
                    filteredSubRoles
                );
                this.userProfile = {
                    ...{
                        ...data.isRoleConfigured[0],
                        subRoles: filteredSubRoles,
                        preferred_username:
                            data.isRoleConfigured[0]?.preferred_username?.toUpperCase(),
                    },
                    profileImage: data.isRoleConfigured[1],
                };
                this.localService.set(
                    LOCALSTORAGEKEYS.USERPROFILE,
                    this.userProfile
                );
            }
        });
    }

    setMetaInforMation(resourceAccess: any, subRoles: string[]) {
        // Clear only permission/profile keys so other session data (e.g. CLF_MEETING_CONTEXT) is preserved on refresh
        [
            LOCALSTORAGEKEYS.USERROLES,
            LOCALSTORAGEKEYS.SUBROLES,
            LOCALSTORAGEKEYS.USERPROFILE,
            LOCALSTORAGEKEYS.NATIONALPERMISSIONS,
            LOCALSTORAGEKEYS.STATEPERMISSIONS,
            LOCALSTORAGEKEYS.DISTRICTPERMISSIONS,
            LOCALSTORAGEKEYS.BLOCKPERMISSIONS,
            LOCALSTORAGEKEYS.APPLICATIONPERMISSIONS,
        ].forEach((key) => localStorage.removeItem(key));

        const aud = Object.keys(resourceAccess);
        const assignedRoles: string[] = [];

        aud.forEach((key: string) => {
            if (
                key !== 'account' &&
                key !== 'broker' &&
                key !== 'realm-management'
            ) {
                resourceAccess[key].roles.forEach((role: string) => {
                    this.getUserGeoLocation(key, role);
                    if (!assignedRoles.includes(role)) {
                        assignedRoles.push(role);
                    }
                });
            }
        });

        const expandedBlockPermissions: any[] = [];
        this.blockPermissions.forEach((permission: any) => {
            if (permission.role === roles.BCIB) {
                subRoles.forEach((subRole: string) => {
                    expandedBlockPermissions.push({
                        ...permission,
                        role: subRole,
                    });
                });
            } else {
                expandedBlockPermissions.push(permission);
            }
        });

        this.blockPermissions = expandedBlockPermissions;

        if (assignedRoles.includes('block-cadre-ib')) {
            const index = assignedRoles.indexOf('block-cadre-ib');
            assignedRoles.splice(index, 1, ...subRoles);
        }

        if (!assignedRoles.length) {
            this.snackbarService.errorSnackbar(
                'You do not have any role access to view this page.'
            );
            this.router.navigate(['/main/role-not-found']);
            this.isLoading = true;
        } else {
            this.localService.set(LOCALSTORAGEKEYS.USERROLES, assignedRoles);
            this.localService.set(
                LOCALSTORAGEKEYS.STATEPERMISSIONS,
                this.statePermissions
            );
            this.localService.set(
                LOCALSTORAGEKEYS.NATIONALPERMISSIONS,
                this.nationalPermissions
            );
            this.localService.set(
                LOCALSTORAGEKEYS.DISTRICTPERMISSIONS,
                this.districtPermissions
            );
            this.localService.set(
                LOCALSTORAGEKEYS.BLOCKPERMISSIONS,
                this.blockPermissions
            );
            this.localService.set(
                LOCALSTORAGEKEYS.APPLICATIONPERMISSIONS,
                this.applicationPermissions
            );
            this.isLoading = true;
        }
    }

    /**
     * Get user geo location
     * @param key
     */
    getUserGeoLocation(key: string, role: string) {
        try {
            const prefix = key.split('-')[0];

            let geoLocation: any;

            switch (prefix) {
                case 'B':
                    geoLocation = this.GEO_LOCATION.find(
                        (geoLocation: any) => geoLocation.block_id === key
                    );
                    this.blockPermissions.push({
                        ...geoLocation,
                        state_id: +geoLocation.state_id.split('-')[1],
                        state_name_en: geoLocation.state_name,
                        state_short_name_en: geoLocation.short_code,
                        district_id: +geoLocation.district_id.split('-')[1],
                        district_name_en: geoLocation.district_name,
                        block_id: +geoLocation.block_id.split('-')[1],
                        block_name_en: geoLocation.block_name,
                        role,
                    });
                    break;
                case 'D':
                    geoLocation = this.GEO_LOCATION.find(
                        (geoLocation: any) => geoLocation.district_id === key
                    );

                    this.districtPermissions.push({
                        ...geoLocation,
                        state_id: +geoLocation.state_id.split('-')[1],
                        state_name_en: geoLocation.state_name,
                        state_short_name_en: geoLocation.short_code,
                        district_id: +geoLocation.district_id.split('-')[1],
                        district_name_en: geoLocation.district_name,
                        block_code: '',
                        block_name: '',
                        block_id: '',
                        role,
                    });
                    break;
                case 'S':
                    geoLocation = this.GEO_LOCATION.find(
                        (geoLocation: any) =>
                            geoLocation.state_id
                                .split('-')[1]
                                .padStart(2, '0') === key.split('-')[1]
                    );
                    this.statePermissions.push({
                        ...geoLocation,
                        state_id: +geoLocation.state_id.split('-')[1],
                        state_name_en: geoLocation.state_name,
                        state_short_name_en: geoLocation.short_code,
                        district_code: '',
                        district_name: '',
                        district_id: '',
                        block_code: '',
                        block_name: '',
                        block_id: '',
                        role,
                    });
                    break;
                case 'N':
                    this.nationalPermissions = this.GEO_LOCATION.filter(
                        (geoLocation: any, index: any, self: any) =>
                            index ===
                            self.findIndex(
                                (x: any) => x.state_id === geoLocation.state_id
                            )
                    ).map((geoLocation: any) => ({
                        // states = sortBy(states, 'state_name');
                        // if (environment.production) {
                        //     states = states.filter((item: any) => !EXCLUDED_STATES.includes(item.state_id));
                        // }
                        state_id: +geoLocation.state_id.split('-')[1],
                        state_name: geoLocation.state_name,
                        state_name_en: geoLocation.state_name,
                        state_short_name_en: geoLocation.short_code,
                    }));
                    break;
                default:
                    this.applicationPermissions.push({
                        appName: key,
                        role,
                    });
            }
        } catch {
            // intentionally left blank
        }
    }
}
